/**
 * WidgetContainer - Wrapper for all widgets with consistent styling
 * and optional collapse/expand functionality
 */

import React, { useState } from 'react';
import { Box, Typography, IconButton, Sheet, Tooltip } from '@mui/joy';
import { ExpandMore as ExpandIcon, ExpandLess as CollapseIcon } from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface WidgetContainerProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  helpText?: string; // Optional help text for the section
  onHelp?: () => void; // Callback to start section-specific tour
  sectionId?: string; // Add ID for section navigation
  audioCue?: string;  // data-audio-cue attribute for AudioGuide pulse system
  audioGuide?: React.ReactNode; // Optional audio guide button rendered in title row
}

export default function WidgetContainer({
  title,
  description,
  children,
  collapsible = false,
  defaultExpanded = true,
  helpText,
  onHelp,
  sectionId,
  audioCue,
  audioGuide,
}: WidgetContainerProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <Sheet
      id={sectionId}
      data-audio-cue={audioCue}
      variant="outlined"
      sx={{
        borderRadius: 'sm',
        p: 2.5,
        bgcolor: 'background.surface',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        boxShadow: 'xs', // Subtle shadow for depth
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: isExpanded ? 2 : 0,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography level="title-lg" sx={{ fontWeight: 600, fontFamily: '"Cinzel", "Garamond", serif', color: '#7a1a2e' }}>
              {title}
            </Typography>
            {helpText && (
              <Tooltip 
                title={helpText} 
                placement="top"
                arrow
                variant="soft"
                size="sm"
                sx={{ 
                  maxWidth: 280,
                  p: 1.5,
                  fontSize: '0.875rem',
                  lineHeight: 1.5
                }}
              >
                <IconButton 
                  size="sm" 
                  variant="plain" 
                  color="primary"
                  onClick={onHelp}
                  sx={{ 
                    cursor: 'help',
                    minWidth: 20,
                    minHeight: 20,
                    padding: '2px',
                    borderRadius: '50%',
                    '&:hover': {
                      bgcolor: 'primary.softBg'
                    }
                  }}
                >
                  <HelpOutlineIcon sx={{ fontSize: '0.95rem' }} />
                </IconButton>
              </Tooltip>
            )}
            {audioGuide}
          </Box>
          {description && (
            <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 0.5 }}>
              {description}
            </Typography>
          )}
        </Box>
        {collapsible && (
          <IconButton
            size="sm"
            variant="plain"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{ ml: 1 }}
          >
            {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        )}
      </Box>

      {isExpanded && (
        <Box 
          sx={{ 
            maxHeight: '600px',
            overflowY: 'auto',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'background.level1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'neutral.outlinedBorder',
              borderRadius: '4px',
              '&:hover': {
                bgcolor: 'neutral.outlinedHoverBorder',
              },
            },
          }}
        >
          {children}
        </Box>
      )}
    </Sheet>
  );
}